import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Checkbox,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { createColumnHelper } from '@tanstack/react-table';
import PropTypes from 'prop-types';
import CommonPagination from 'components/commonPagination/CommonPagination';
import CommonTable from 'components/commonTable/CommonTable';
import { showErrorAlert, showSuccessAlert } from 'utils/AlertUtils';
import * as labelApi from 'utils/labelApi';

const columnHelper = createColumnHelper();
const SEARCH_DEBOUNCE_MS = 400;

/**
 * Assign merchants to a label from the label's own page (RAT-1689).
 *
 * Tracks a DELTA (pending adds / pending removes) rather than "the set of
 * checked rows": the list is paginated and role-scoped, so a full set would be
 * missing every merchant the caller cannot see and saving it would unassign
 * them. Only what the user actually toggled is sent.
 *
 * Built from CommonTable/CommonPagination rather than reusing the price-strategy
 * MerchantAssignModal: that one reads the merchant-list thunk and returns a
 * whole selection, while this needs the label-scoped feed (which narrows to the
 * label's region and flags what is already assigned) and delta output.
 * Parameterising it for both would have meant changing the price-strategy flow.
 */
const LabelMerchantAssignModal = ({
  isOpen,
  onClose,
  labelId,
  labelName,
  onSaved,
}) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingAdd, setPendingAdd] = useState([]);
  const [pendingRemove, setPendingRemove] = useState([]);
  const debounceRef = useRef(null);

  useEffect(() => {
    debounceRef.current = setTimeout(
      () => setDebouncedSearch(search),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  // A new search starts from page 1 - otherwise a narrower result set can leave
  // the user on an empty page.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  const fetchRows = useCallback(async () => {
    if (!labelId) return;
    setIsLoading(true);
    try {
      const data = await labelApi.getAssignableMerchants(labelId, {
        search: debouncedSearch,
        page,
        limit,
      });
      setRows(data?.list || []);
      setTotal(data?.total || 0);
      setLimit(data?.limit || 10);
    } catch (error) {
      showErrorAlert(
        error?.message || error?.error?.message || 'Failed to load merchants',
      );
    } finally {
      setIsLoading(false);
    }
  }, [labelId, debouncedSearch, page, limit]);

  useEffect(() => {
    if (isOpen) fetchRows();
  }, [isOpen, fetchRows]);

  // Reopening starts clean - a half-finished delta from a cancelled session must
  // not silently apply later.
  useEffect(() => {
    if (!isOpen) {
      setPendingAdd([]);
      setPendingRemove([]);
      setSearch('');
      setPage(1);
    }
  }, [isOpen]);

  const isChecked = useCallback(
    (row) =>
      row.assigned
        ? !pendingRemove.includes(row.merchantId)
        : pendingAdd.includes(row.merchantId),
    [pendingAdd, pendingRemove],
  );

  const toggle = useCallback((row) => {
    const id = row.merchantId;
    if (row.assigned) {
      setPendingRemove((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
      );
      return;
    }
    setPendingAdd((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: () => null,
        cell: ({ row }) => (
          <Checkbox
            isChecked={isChecked(row.original)}
            onChange={() => toggle(row.original)}
          />
        ),
      }),
      columnHelper.accessor((row) => row?.merchantId, {
        id: 'merchantId',
        header: () => (
          <Text fontSize="12px" color="gray.400">
            Merchant ID
          </Text>
        ),
        cell: (info) => <Text fontSize="sm">{info.getValue()}</Text>,
      }),
      columnHelper.accessor((row) => row?.name, {
        id: 'name',
        header: () => (
          <Text fontSize="12px" color="gray.400">
            Merchant Name
          </Text>
        ),
        cell: (info) => (
          <Text fontSize="sm" fontWeight="600">
            {info.getValue()}
          </Text>
        ),
      }),
      columnHelper.accessor((row) => row?.regionName, {
        id: 'regionName',
        header: () => (
          <Text fontSize="12px" color="gray.400">
            Region
          </Text>
        ),
        cell: (info) => <Text fontSize="sm">{info.getValue()}</Text>,
      }),
    ],
    [isChecked, toggle],
  );

  const hasChanges = pendingAdd.length > 0 || pendingRemove.length > 0;

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const result = await labelApi.assignLabelMerchants(labelId, {
        add: pendingAdd,
        remove: pendingRemove,
      });
      // Report what the server actually changed, not what was requested - a
      // re-sent row is a no-op rather than an error.
      showSuccessAlert(
        `${result?.added ?? 0} assigned, ${result?.removed ?? 0} removed`,
      );
      onSaved?.();
      onClose();
    } catch (error) {
      showErrorAlert(
        error?.message || error?.error?.message || 'Failed to update merchants',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    // Full-screen below md: the merchant table has a 600px minimum, so a fixed
    // 3xl dialog hangs off the side of a phone viewport instead of scrolling.
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: 'full', md: '3xl' }}
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent
        mx={{ base: 0, md: 4 }}
        my={{ base: 0, md: 4 }}
        maxH={{ base: '100dvh', md: '90vh' }}
        borderRadius={{ base: 0, md: 'md' }}
      >
        <ModalHeader pr="12">
          <Flex
            align={{ base: 'stretch', md: 'center' }}
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            gap="3"
          >
            <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="semibold">
              Assign merchants to {labelName}
            </Text>
            <InputGroup w={{ base: '100%', md: '280px' }} size="sm">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search by merchant name or ID"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                borderRadius="md"
              />
            </InputGroup>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto" px={{ base: 3, md: 6 }}>
          {/* CommonTable is tableLayout:fixed and defaults every column to
              200px, so the checkbox column has to be sized explicitly or it
              takes as much room as a name. Same map the price-strategy assign
              modal uses. */}
          <CommonTable
            columns={columns}
            data={rows}
            isLoading={isLoading}
            keyValue="merchantId"
            rowclick={[]}
            dynamicWidth={{
              select: '56px',
              merchantId: '110px',
              name: '240px',
              regionName: '120px',
            }}
            emptyStateMessage="No merchants available for this label."
          />

          {total > limit && (
            <CommonPagination
              totalCount={total}
              activePage={page}
              pageCount={Math.ceil(total / (limit || 10))}
              onPageChange={setPage}
              itemsPerPage={limit || 10}
              onItemsPerPageChange={handleLimitChange}
            />
          )}
        </ModalBody>
        <ModalFooter px={{ base: 3, md: 6 }}>
          {/* Stacks on a phone - the pending-change summary and both buttons do
              not fit on one line at that width. */}
          <Flex
            align={{ base: 'stretch', sm: 'center' }}
            direction={{ base: 'column', sm: 'row' }}
            justify="space-between"
            w="100%"
            gap="3"
          >
            <Text fontSize="sm" color="gray.500">
              {hasChanges
                ? `${pendingAdd.length} to assign, ${pendingRemove.length} to remove`
                : 'No changes'}
            </Text>
            <Flex gap="3" justify={{ base: 'stretch', sm: 'flex-end' }}>
              <Button
                variant="outline"
                onClick={onClose}
                flex={{ base: 1, sm: 'initial' }}
              >
                Cancel
              </Button>
              <Button
                bg="primary"
                color="white"
                _hover={{ bg: 'primary', opacity: 0.8 }}
                isDisabled={!hasChanges}
                isLoading={isSubmitting}
                onClick={handleSave}
                flex={{ base: 1, sm: 'initial' }}
              >
                Save
              </Button>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

LabelMerchantAssignModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  labelId: PropTypes.string,
  labelName: PropTypes.string,
  onSaved: PropTypes.func,
};

export default LabelMerchantAssignModal;
